import getQueryUrl from '../http/getQueryUrl';
import http from '../http';
import database from '../database';
import paged from '../filters/paged';
import logging from '@sstdev/lib_logging';

const _p = {
    http,
    database
};
export const _private = _p;

/**
 * Fetches server-side query results and upserts them into the corresponding local
 * collection. Records are tagged with `meta.forTempQueryResult = queryId` for
 * truncation/eviction protection (the display gate keys off the view's own result
 * _ids — see forTempQueryResult.js / #898 — not this tag).
 *
 * Records are permanently retained locally — eviction is handled by truncation
 * with active-view protection (see ADR-GF-001).
 *
 * @param {object} dbView
 * @param {string} queryId - unique identifier tagged onto each inserted record's meta.
 * @param {string[]} [restrictToIds] - fetch exactly these records by _id instead of running the
 * view's own query. Used by the stage-2 disagreement resolution (#1978), which needs the server's
 * current values for a set of rows, NOT the server's opinion of whether they match the filter.
 * @returns {Promise<{ result: any[], failedIds: string[] }>} `failedIds` are records the server
 * returned that did NOT make it into the local collection. `bulkUpsert` reports a per-record
 * failure through a count rather than by rejecting, so without this a caller cannot tell a row
 * holding the server's current values from one still holding the stale values it had before.
 */
export default async function fetchAndUpsertTemporaryQueryResult(dbView, queryId, restrictToIds) {
    if (queryId == null) {
        throw new Error('A queryId must be included when calling fetchAndUpsertTemporaryQueryResult');
    }
    // Avoids 'Cannot access 'database' before initialization' error for unit tests
    _p.database = _p.database ?? database;

    const { namespace, relation } = dbView;
    const filters = getRequestFilters(dbView, restrictToIds);
    // Get the query results from the server side
    const queryUrl = getQueryUrl(namespace, relation, filters);
    const httpResult = await _p.http.get(queryUrl);
    const records = httpResult?.items ?? [];

    if (!records || records.length === 0) return { result: records, failedIds: [] };

    // Tag each record with queryId — consumed by truncation/eviction protection (the display
    // gate now keys off the view's own result _ids, see forTempQueryResult.js / #898).
    // Set on each record directly (not via `mergeIntoInserts`) so bulkUpsert's UPDATE path also
    // writes the tag — otherwise a record already in the cache (e.g. from background sync) would
    // keep its untagged meta and be evicted by truncation. Matches fullTextSearch.js's pattern.
    // Records are permanently retained locally; eviction is handled by truncation with active-view
    // protection (see ADR-GF-001).
    for (const record of records) {
        record.meta = { ...record.meta, forTempQueryResult: queryId };
    }
    const db = _p.database.get();
    const upsertResult = await db.bulkUpsert(namespace, relation, records);
    const failedIds = upsertResult?.failedIds ?? [];
    if (failedIds.length > 0) {
        logging.warn(
            `[DBVIEW] ${namespace}:${relation}: ${failedIds.length} of ${records.length} refreshed record(s) ` +
                'did not reach the local collection; their local values are still the old ones.'
        );
    }
    return { result: records, failedIds };
}

function getRequestFilters(dbView, restrictToIds) {
    if (restrictToIds?.length > 0) {
        // A by-_id refresh (#1978). The user's find criteria are DELIBERATELY left off: the
        // caller is asking what these rows currently look like, and re-applying the criteria
        // here would make the server the judge of its own answer. Tenant and use-case scoping
        // still bound the response — those live on the request, not in these filters.
        return {
            searchCriteria: { _id: { $in: restrictToIds.map(asServerId) } },
            paged: paged.getFilter(1, restrictToIds.length, 0)
        };
    }

    const filters = dbView.viewCriteria.getFiltersFromView();
    // getFiltersFromView echoes the Phase 3 gate's bbFilter (forTempQueryResult, #332)
    // once stage 2 has applied. getQueryUrl already guards via getUriComponent, but
    // strip locally so downstream URL builders never see it (defense in depth).
    delete filters.forTempQueryResult;
    // paged is not an MQL filter implemented on the client side, so it won't be stored in the
    // loki dynamicview filterPipeline (and therefore won't be returned by getFiltersFromView
    // above)
    filters.paged = paged.getFilter(dbView.page, undefined, dbView.serverSideOffset ?? 0);
    return filters;
}

// Mirrors what searchCriteria does for a scalar `*_id` value: a 24-character hex string is an
// ObjectId, and the server's bson.normalize turns { $oid } into one at any depth, arrays
// included. A relation whose ids really are strings is sent unchanged.
function asServerId(_id) {
    return /^[0-9a-f]{24}$/i.test(_id) ? { $oid: _id } : _id;
}
